import { parseListQueryState, serializeListQueryState } from "./listQuery";
import type {
  FilterDefinition,
  ListQueryCodecConfig,
  ListQueryState,
  SortState,
} from "./types";

export type FilesTab = "search" | "explorer" | "export";

export type FilesListFilters = Pick<
  FileSearchState,
  | "source"
  | "docType"
  | "dateFrom"
  | "dateTo"
  | "registration"
  | "departureAirport"
  | "arrivalAirport"
  | "flightId"
  | "flags"
>;

export const DEFAULT_FILE_SEARCH_STATE: FileSearchState = {
  q: "",
  source: [],
  docType: [],
  dateFrom: null,
  dateTo: null,
  registration: null,
  departureAirport: null,
  arrivalAirport: null,
  flightId: null,
  flags: [],
  page: 1,
  pageSize: 25,
  sortBy: "relevance",
  sortDirection: "desc",
};

export const DEFAULT_FILES_LIST_FILTERS: FilesListFilters = {
  source: [],
  docType: [],
  dateFrom: null,
  dateTo: null,
  registration: null,
  departureAirport: null,
  arrivalAirport: null,
  flightId: null,
  flags: [],
};

const FILE_SOURCE_VALUES = new Set<FilesSourceKind>([
  "flights",
  "doc-destinations",
  "library",
]);
const FILE_SORT_VALUES = new Set<FileSearchSortBy>(["relevance", "name", "date", "size"]);

const normalizeText = (value: unknown): string | null => {
  const normalized = typeof value === "string" ? value.trim() : "";
  return normalized || null;
};

const normalizeAirport = (value: unknown): string | null =>
  normalizeText(value)?.toUpperCase() || null;

const normalizeStringList = (value: unknown): string[] => Array.isArray(value)
  ? Array.from(new Set(value.map((entry) => normalizeText(entry)).filter(Boolean) as string[])).sort()
  : [];

const textFilter = (
  key: "dateFrom" | "dateTo" | "registration" | "flightId",
  label: string,
  inputType: "text" | "date",
): FilterDefinition<FilesListFilters> => ({
  key,
  label,
  inputType,
  defaultValue: null,
  parse: (values) => values[0] || null,
  serialize: (value) => normalizeText(value) ? [normalizeText(value)!] : [],
  normalize: normalizeText,
  isActive: (value) => Boolean(normalizeText(value)),
  toChipLabel: (value) => `${label}: ${normalizeText(value) || ""}`,
});

const airportFilter = (
  key: "departureAirport" | "arrivalAirport",
  label: string,
): FilterDefinition<FilesListFilters> => ({
  key,
  label,
  inputType: "text",
  defaultValue: null,
  parse: (values) => values[0] || null,
  serialize: (value) => normalizeAirport(value) ? [normalizeAirport(value)!] : [],
  normalize: normalizeAirport,
  isActive: (value) => Boolean(normalizeAirport(value)),
  toChipLabel: (value) => `${label}: ${normalizeAirport(value) || ""}`,
});

const arrayFilter = (
  key: "source" | "docType" | "flags",
  label: string,
  normalize: (value: unknown) => string[] = normalizeStringList,
): FilterDefinition<FilesListFilters> => ({
  key,
  label,
  inputType: "multi-select",
  defaultValue: [],
  parse: (values) => values,
  serialize: (value) => normalize(value),
  normalize,
  isActive: (value) => normalize(value).length > 0,
  toChipLabel: (value) => `${label}: ${normalize(value).join(", ")}`,
});

const normalizeSources = (value: unknown): FilesSourceKind[] =>
  normalizeStringList(value).filter((entry): entry is FilesSourceKind => (
    FILE_SOURCE_VALUES.has(entry as FilesSourceKind)
  ));

export const filesListFilterDefinitions: FilterDefinition<FilesListFilters>[] = [
  arrayFilter("source", "Source", normalizeSources),
  arrayFilter("docType", "Document type"),
  textFilter("dateFrom", "Date from", "date"),
  textFilter("dateTo", "Date to", "date"),
  textFilter("registration", "Registration", "text"),
  airportFilter("departureAirport", "Departure"),
  airportFilter("arrivalAirport", "Arrival"),
  textFilter("flightId", "Flight ID", "text"),
  arrayFilter("flags", "Flag"),
];

export const filesListQueryConfig: ListQueryCodecConfig<FilesListFilters> = {
  filters: filesListFilterDefinitions,
  defaultFilters: DEFAULT_FILES_LIST_FILTERS,
  defaultSort: { key: "relevance", direction: "desc" },
  defaultPagination: { mode: "page", page: 1, pageSize: 25 },
  searchParameter: "q",
  sortParameter: "sortBy",
  sortDirectionParameter: "sortDirection",
};

const normalizeSort = (sort: SortState | null): {
  sortBy: FileSearchSortBy;
  sortDirection: FileSearchSortDirection;
} => ({
  sortBy: sort && FILE_SORT_VALUES.has(sort.key as FileSearchSortBy)
    ? sort.key as FileSearchSortBy
    : DEFAULT_FILE_SEARCH_STATE.sortBy,
  sortDirection: sort?.direction || DEFAULT_FILE_SEARCH_STATE.sortDirection,
});

export const toFilesListQueryState = (
  state: FileSearchState,
): ListQueryState<FilesListFilters> => ({
  search: state.q,
  filters: {
    source: normalizeSources(state.source),
    docType: normalizeStringList(state.docType),
    dateFrom: normalizeText(state.dateFrom),
    dateTo: normalizeText(state.dateTo),
    registration: normalizeText(state.registration),
    departureAirport: normalizeAirport(state.departureAirport),
    arrivalAirport: normalizeAirport(state.arrivalAirport),
    flightId: normalizeText(state.flightId),
    flags: normalizeStringList(state.flags),
  },
  sort: {
    key: FILE_SORT_VALUES.has(state.sortBy) ? state.sortBy : DEFAULT_FILE_SEARCH_STATE.sortBy,
    direction: state.sortDirection === "asc" ? "asc" : "desc",
  },
  pagination: {
    mode: "page",
    page: Number.isFinite(state.page) && state.page > 0 ? state.page : 1,
    pageSize: Number.isFinite(state.pageSize) && state.pageSize > 0 ? state.pageSize : 25,
  },
});

export const fromFilesListQueryState = (
  state: ListQueryState<FilesListFilters>,
): FileSearchState => {
  const sort = normalizeSort(state.sort);
  const pagination = state.pagination.mode === "page"
    ? state.pagination
    : filesListQueryConfig.defaultPagination;

  return {
    q: state.search,
    source: normalizeSources(state.filters.source),
    docType: normalizeStringList(state.filters.docType),
    dateFrom: normalizeText(state.filters.dateFrom),
    dateTo: normalizeText(state.filters.dateTo),
    registration: normalizeText(state.filters.registration),
    departureAirport: normalizeAirport(state.filters.departureAirport),
    arrivalAirport: normalizeAirport(state.filters.arrivalAirport),
    flightId: normalizeText(state.filters.flightId),
    flags: normalizeStringList(state.filters.flags),
    page: pagination.mode === "page" ? pagination.page : 1,
    pageSize: pagination.mode === "page" ? pagination.pageSize : 25,
    ...sort,
  };
};

export const parseFilesSearchState = (params: URLSearchParams): FileSearchState =>
  fromFilesListQueryState(parseListQueryState(params, filesListQueryConfig));

export const isFilesTab = (value: string | null): value is FilesTab =>
  value === "search" || value === "explorer" || value === "export";

export const serializeFilesPageParams = (
  activeTab: FilesTab,
  state: FileSearchState,
  currentParams: URLSearchParams = new URLSearchParams(),
): URLSearchParams => {
  const params = serializeListQueryState(
    toFilesListQueryState(state),
    filesListQueryConfig,
    currentParams,
  );

  if (activeTab === "search") {
    params.delete("tab");
  } else {
    params.set("tab", activeTab);
  }
  params.sort();
  return params;
};

export const isFileSearchStateEqual = (left: FileSearchState, right: FileSearchState): boolean =>
  JSON.stringify(toFilesListQueryState(left)) === JSON.stringify(toFilesListQueryState(right));

export const extractDebouncedFileSearchFields = (state: FileSearchState) => ({
  q: state.q,
  source: state.source,
  docType: state.docType,
  dateFrom: state.dateFrom,
  dateTo: state.dateTo,
  registration: state.registration,
  departureAirport: state.departureAirport,
  arrivalAirport: state.arrivalAirport,
  flightId: state.flightId,
  flags: state.flags,
});
